import { ChangeDetectionStrategy, Component, computed, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

import { Button, ButtonIcon, ButtonLabel } from 'primeng/button';

import { TranslationModule } from '@core/translation/translation.module';
import { U2bDialogComponent } from '@u2bUi/dialog/u2b-dialog.component';
import { U2bIconButtonComponent } from '@u2bUi/icon-button/u2b-icon-button.component';

interface TourStep {
    icon: string;
    titleKey: string;
    descriptionKey: string;
    tipKey?: string;
}

const TOUR_STEPS: TourStep[] = [
    {
        icon: 'menu_book',
        titleKey: 'bookingV2.tour.steps.welcome.title',
        descriptionKey: 'bookingV2.tour.steps.welcome.description',
        tipKey: 'bookingV2.tour.steps.welcome.tip',
    },
    {
        icon: 'menu_book',
        titleKey: 'bookingV2.tour.steps.type.title',
        descriptionKey: 'bookingV2.tour.steps.type.description',
        tipKey: 'bookingV2.tour.steps.type.tip',
    },
    {
        icon: 'event',
        titleKey: 'bookingV2.tour.steps.date.title',
        descriptionKey: 'bookingV2.tour.steps.date.description',
        tipKey: 'bookingV2.tour.steps.date.tip',
    },
    {
        icon: 'anchor',
        titleKey: 'bookingV2.tour.steps.berth.title',
        descriptionKey: 'bookingV2.tour.steps.berth.description',
        tipKey: 'bookingV2.tour.steps.berth.tip',
    },
    {
        icon: 'person',
        titleKey: 'bookingV2.tour.steps.person.title',
        descriptionKey: 'bookingV2.tour.steps.person.description',
        tipKey: 'bookingV2.tour.steps.person.tip',
    },
    {
        icon: 'settings',
        titleKey: 'bookingV2.tour.steps.advanced.title',
        descriptionKey: 'bookingV2.tour.steps.advanced.description',
        tipKey: 'bookingV2.tour.steps.advanced.tip',
    },
    {
        icon: 'check_circle',
        titleKey: 'bookingV2.tour.steps.summary.title',
        descriptionKey: 'bookingV2.tour.steps.summary.description',
        tipKey: 'bookingV2.tour.steps.summary.tip',
    },
];

@Component({
    selector: 'booking-v2-onboarding-tour',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, Button, ButtonIcon, ButtonLabel, TranslationModule, U2bDialogComponent, U2bIconButtonComponent],
    templateUrl: './onboarding-tour.component.html',
    host: { class: 'contents' },
})
export class OnboardingTourComponent {

    readonly closeTour = output<void>();

    readonly steps = TOUR_STEPS;
    readonly stepIndex = signal<number>(0);
    readonly currentStep = computed(() => this.steps[this.stepIndex()]);
    readonly isLast = computed(() => this.stepIndex() === this.steps.length - 1);
    readonly isFirst = computed(() => this.stepIndex() === 0);
    readonly totalSteps = this.steps.length;

    goTo(index: number): void {
        if (index >= 0 && index < this.steps.length) {
            this.stepIndex.set(index);
        }
    }

    next(): void {
        if (this.isLast()) {
            this.close();
            return;
        }
        this.stepIndex.update((i) => i + 1);
    }

    previous(): void {
        this.stepIndex.update((i) => Math.max(0, i - 1));
    }

    close(): void {
        this.closeTour.emit();
    }
}
